import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import objectSupport from 'dayjs/plugin/objectSupport';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { Filter, DayRow, NoInformation } from '../../index';

import {
    StyledSessionTableContainer,
    StyledSessionTableHeader,
    StyledSessionTableBody,
} from './style';

import { alertError } from '../../../redux/stateActions';
import { GET_ONE_ON_ONE_MEETS } from '../../../services/provider';

dayjs.extend(objectSupport);

const OneOnOneSession = ({ staffOptions, selectedStaff, setSelectedStaff }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const user = useSelector((state) => state.user);

    /*--------------- Get OneOnOne session ---------------*/
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [oneOnOneMeet, setOneOnOneMeet] = useState([]);

    const variables = useMemo(() => {
        const newVariables = {
            hospital: user.staff.hospital.id,
            startDate: startDate,
            endDate: endDate,
        };

        if (selectedStaff !== '') {
            newVariables.staff = selectedStaff;
        }
        return newVariables;
    }, [selectedStaff, startDate, endDate]);

    const { data: getOneOnOneMeetsData, error: getOneOnOneMeetsError } = useQuery(
        GET_ONE_ON_ONE_MEETS,
        {
            variables: variables,
            skip: _.isEmpty(user) || !startDate || !endDate,
            fetchPolicy: "network-only",
        }
    );

    useEffect(() => {
        if (getOneOnOneMeetsError) {
            dispatch(alertError(`Cannot get 1on1 data: ${getOneOnOneMeetsError.message}`));
        }
        if (getOneOnOneMeetsData) {
            const newOneOnOneMeets = [];

            getOneOnOneMeetsData.meets.forEach((meet) => {
                const findIndex = newOneOnOneMeets.findIndex((m) => m.date === meet.startDate);

                if (findIndex !== -1) {
                    newOneOnOneMeets[findIndex].meets.push(meet);
                } else {
                    newOneOnOneMeets.push({
                        date: meet.startDate,
                        meets: [meet],
                    });
                }
            });

            setOneOnOneMeet(newOneOnOneMeets);
        }
    }, [getOneOnOneMeetsData, getOneOnOneMeetsError]);

    const handleFilterChange = ({ isToday, selectedMonth, selectedYear }) => {
        if (isToday) {
            setStartDate(dayjs().startOf('date').format('YYYY-MM-DD'));
            setEndDate(dayjs().endOf('date').format('YYYY-MM-DD'));
        } else {
            setStartDate(
                dayjs()
                    .set({ month: selectedMonth, year: selectedYear })
                    .startOf('month')
                    .format('YYYY-MM-DD')
            );
            setEndDate(
                dayjs()
                    .set({ month: selectedMonth, year: selectedYear })
                    .endOf('month')
                    .format('YYYY-MM-DD')
            );
        }
    };

    const handleRedirect = (id) => {
        history.push({
            pathname: `/provider/session/one/${id}`,
            state: {
                prevPath: history.location.pathname,
                prevTab: 2,
            },
        });
    };

    return (
        <StyledSessionTableContainer>
            <StyledSessionTableHeader>
                <Filter
                    name="All 1 on 1 Session"
                    onChange={handleFilterChange}
                    staffOptions={staffOptions}
                    selectedStaff={selectedStaff}
                    setSelectedStaff={setSelectedStaff}
                />
            </StyledSessionTableHeader>
            <StyledSessionTableBody>
                {!_.isEmpty(oneOnOneMeet) ? (
                    oneOnOneMeet.map((item) => (
                        <DayRow key={item.date} type="one" data={item} onClick={handleRedirect} />
                    ))
                ) : (
                    <NoInformation />
                )}
            </StyledSessionTableBody>
        </StyledSessionTableContainer>
    );
};

OneOnOneSession.propTypes = {
    staffOptions: PropTypes.arrayOf(PropTypes.object),
    selectedStaff: PropTypes.string.isRequired,
    setSelectedStaff: PropTypes.func.isRequired,
};

export default OneOnOneSession;
